import { Tag } from 'antd';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { PensionerStatus } from '../../../utils/constants/tagColors';

/**
 * Custom column configurations for different types of MailSender entries (pensioners, owners, and veterinarians/groomers).
 *
 * @function
 * @param {Object} props - The component props.
 * @param {Object} props.enums - An object containing enumerated values.
 * @returns {Object} An object containing column configurations for pensioners, owners, and veterinarians/groomers.
 */

const createSendingDateColumn = (t, titleTKey, key) => ({
  title: t(titleTKey),
  key,
  dataIndex: key,
  render: (value) => value && dayjs(value).format('DD/MM/YYYY'),
  sorter: true
});

const createCityColumn = (t, tKeyPrefix) => ({
  title: t(`${tKeyPrefix}.city`),
  key: 'address.city',
  dataIndex: 'address',
  render: (address) => address?.city
});

const createStatusColumn = (t, tKeyPrefix, enums) => ({
  title: t(`${tKeyPrefix}.status`),
  key: 'status',
  dataIndex: 'status',
  render: (status) =>
    status ? (
      <Tag color={PensionerStatus[status]}>
        {t(`${tKeyPrefix}.tags.${status}`, { defaultValue: status })}
      </Tag>
    ) : (
      'N/R'
    ),
  sorter: true,
  filters: enums?.status?.map((r) => ({
    text: t(`${tKeyPrefix}.tags.${r}`),
    value: r
  }))
});

export const useColumns = ({ enums, type }) => {
  const { t } = useTranslation();
  const pensionerTKey = 'mail-sender.form.pensioners';
  const ownerTKey = 'mail-sender.form.owners';
  const sendingDateTKey = (prefix) => `${prefix}.sending_date`;

  const pensionerColumns = [
    {
      title: t('mail-sender.form.pensioners.reference'),
      key: 'reference',
      dataIndex: 'reference',
      sorter: true
    },
    {
      title: t('mail-sender.form.pensioners.name'),
      key: 'last_name',
      dataIndex: 'last_name',
      sorter: true
    },
    ...(type === 'ANOUNCEMENT'
      ? [
          createSendingDateColumn(
            t,
            sendingDateTKey(pensionerTKey),
            'anouncement_mail_sending_date'
          )
        ]
      : []),
    ...(type === 'PENSIONER_WISH'
      ? [
          createSendingDateColumn(
            t,
            sendingDateTKey(pensionerTKey),
            'wish_mail_sending_date'
          )
        ]
      : []),
    ...(type === 'OTHER_PENSIONER'
      ? [
          createSendingDateColumn(
            t,
            sendingDateTKey(pensionerTKey),
            'other_mail_sending_date'
          )
        ]
      : []),
    ...(type === 'PENSIONER_EXPLANATORY_DOCUMENT'
      ? [
          createSendingDateColumn(
            t,
            'mail-sender.form.owners.sending_date',
            'explanatory_document_sending_date'
          )
        ]
      : []),
    createCityColumn(t, pensionerTKey),
    createStatusColumn(t, pensionerTKey, enums)
  ];

  const ownerColumns = [
    {
      title: t('mail-sender.form.owners.reference'),
      key: 'reference',
      dataIndex: 'reference',
      sorter: true
    },
    {
      title: t('mail-sender.form.owners.name'),
      key: 'last_name',
      dataIndex: 'last_name',
      sorter: true
    },
    ...(type === 'OWNER_WISH'
      ? [
          createSendingDateColumn(
            t,
            sendingDateTKey(ownerTKey),
            'owner_wish_sending_date'
          )
        ]
      : []),
    ...(type === 'OTHER_OWNER'
      ? [
          createSendingDateColumn(
            t,
            sendingDateTKey(ownerTKey),
            'other_mail_sending_date'
          )
        ]
      : []),
    ...(type === 'OWNER_EXPLANATORY_DOCUMENT'
      ? [
          createSendingDateColumn(
            t,
            sendingDateTKey(ownerTKey),
            'explanatory_document_sending_date'
          )
        ]
      : []),
    createCityColumn(t, ownerTKey),
    createStatusColumn(t, ownerTKey, enums)
  ];

  const veterinarianAndGroomerColumns = [
    {
      title: t('mail-sender.form.veterinarian_and_groomer.type'),
      key: 'type',
      dataIndex: 'type',
      render: (el) =>
        el && t(`mail-sender.form.veterinarian_and_groomer.types.${el}`),
      filters: [
        {
          text: t(
            `mail-sender.form.veterinarian_and_groomer.types.veterinarian`
          ),
          value: 'veterinarian'
        },
        {
          text: t(`mail-sender.form.veterinarian_and_groomer.types.groomer`),
          value: 'groomer'
        }
      ],
      onFilter: (value, record) => record.type === value
    },
    {
      title: t('mail-sender.form.veterinarian_and_groomer.name'),
      key: 'name',
      dataIndex: 'name',
      sorter: true
    },
    {
      title: t('mail-sender.form.veterinarian_and_groomer.city'),
      key: 'address.city',
      dataIndex: 'address',
      render: (address) => address?.city,
      sorter: true
    },
    {
      title: t('mail-sender.form.veterinarian_and_groomer.reference'),
      key: 'owner',
      dataIndex: 'owner',
      render: (owner) => owner?.reference,
      sorter: true
    },
    {
      title: t('mail-sender.form.veterinarian_and_groomer.sending_date'),
      key: 'advert_sending_date',
      dataIndex: 'advert_sending_date',
      render: (advert_sending_date) =>
        advert_sending_date && dayjs(advert_sending_date).format('DD/MM/YYYY'),
      sorter: true
    }
  ];

  return { pensionerColumns, ownerColumns, veterinarianAndGroomerColumns };
};
